import type { Cage, CageApiId } from '@/model/Cage.model';
import { Select } from '@/components/UI/FormFields/Latest';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { successToast, errorToast } from '@/helpers';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { type StudyApiId } from '@/model/Study.model.ts';

interface CageReorderRequest {
  target_cage_api_id: CageApiId | undefined;
  position: 'before' | 'after';
}

interface SelectOption {
  value: CageApiId;
  label: string;
}

interface ReorderCageProps {
  cage: Cage;
  selectOptions: Array<SelectOption>;
  studyApiId: StudyApiId;
  closeModal: () => void;
  fetchCages: () => void;
}

export const ReorderCage: FC<ReorderCageProps> = ({ cage, selectOptions, studyApiId, fetchCages, closeModal }) => {
  const { invoke: reorderCage, loading: isSubmitting } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/cages/reorder',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const onSubmit = async (update: CageReorderRequest) => {
    // Type guard to ensure target_cage_api_id is defined
    if (!update.target_cage_api_id) {
      errorToast('Please select a target cage');
      return;
    }

    try {
      const reorderResponse = await reorderCage({
        body: {
          cage_api_id: cage.api_id,
          target_cage_api_id: update.target_cage_api_id,
          position: update.position,
        },
        path: { studyApiId },
      });

      if (reorderResponse?.type === 'success') {
        successToast('Cage reordered successfully');
        fetchCages(); // Refresh the cage list
        closeModal();
      } else {
        errorToast('Failed to reorder cage');
      }
    } catch (error) {
      errorToast('Failed to reorder cage');
    }
  };

  const formMethods = useForm<CageReorderRequest>({
    mode: 'onChange',
    defaultValues: {
      position: 'before' as const,
      target_cage_api_id: undefined,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty, isSubmitting: formSubmitting },
  } = formMethods;

  // selectOptions are already pre-processed by the parent component

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title="Reorder cage"
        subText={cage.name}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        data-testid="modal-header"
      />
      <FormProvider {...formMethods}>
        <form data-test-component="ReorderCage" data-test-element="form" onSubmit={handleSubmit(onSubmit)}>
          <div data-test-element="position-section" className="ph3 pv2">
            <label className="mb2 db" data-testid="position-label">
              Move cage '{cage.name}' to
            </label>
            <Controller
              name="position"
              control={control}
              rules={{ required: 'Please select a position' }}
              render={({ field: { onChange, value } }) => (
                <div data-test-element="position-options" className="flex items-center">
                  <Radio
                    id="position-before"
                    name="position"
                    label="Before"
                    value="before"
                    checked={value === 'before'}
                    onChange={(e) => onChange(e.target.value)}
                    className="pointer"
                    dataTestId="position-before"
                  />
                  <Radio
                    id="position-after"
                    name="position"
                    label="After"
                    value="after"
                    checked={value === 'after'}
                    onChange={(e) => onChange(e.target.value)}
                    className="pointer ml3"
                    dataTestId="position-after"
                  />
                </div>
              )}
            />
          </div>
          <div data-test-element="cage-selection" className="ph3 pv2">
            <Select label="Cage" required name="target_cage_api_id" options={selectOptions} />
          </div>
        </form>
      </FormProvider>
      <ModalActions
        data-test-element="actions"
        className="pa3 mt3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitButtonProps={{
          disabled: !isValid || !isDirty || isSubmitting || formSubmitting,
          'data-testid': 'submit-button',
        }}
        cancelButtonProps={{ 'data-testid': 'cancel-button' }}
      />
    </ModalContainer>
  );
};
